/**
 * IdentityBackfillCard — Phase 8.LP.LR v1.2 (Owner Console UI)
 * ------------------------------------------------------------
 * Owner/Admin-only UI wrapper around the existing admin endpoint
 * ``POST /api/admin/companies/identity/backfill``.
 *
 * Purpose:
 *   Give Vincent a one-click way to populate ``company_identity_cache``
 *   for a list of launch tickers WITHOUT touching Postman/CURL. The
 *   backend guarantees this only fetches FMP /profile — no LLM calls,
 *   no analysis runs, no valuation / portfolio / watchlist writes.
 *
 * This card also exposes a second read-only helper:
 *   * "Check cached identities" → ``GET /api/companies/identity``
 *     which is cache-only (does not touch FMP) and returns whatever
 *     the display surfaces will see on next page load.
 *
 * The card is gated behind the ``adminHeadersFn()`` factory — non-admin
 * sessions hitting the backend will receive HTTP 401. The factory is
 * passed in so this component stays decoupled from OwnerConsole's
 * private session storage helpers (and is trivially testable).
 */
import React, { useState } from "react";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";

const LAUNCH_TICKERS = "AAPL, MSFT, NVDA, TSLA, JPM, GOOGL, META, V, MU, JNJ, PYPL, INTC";

export function _parseTickers(raw) {
  if (!raw) return [];
  const seen = new Set();
  const out = [];
  for (const chunk of String(raw).split(/[\s,;\n]+/)) {
    const t = chunk.trim().toUpperCase();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

function _statusStyle(status) {
  switch (status) {
    case "created":
    case "updated":
      return "bg-emerald-500/10 text-emerald-300 border-emerald-500/40";
    case "skipped_fresh":
      return "bg-sky-500/10 text-sky-300 border-sky-500/40";
    case "skipped_logo_missing_suppressed":
      return "bg-amber-500/10 text-amber-300 border-amber-500/40";
    case "skipped_fmp_disabled":
      return "bg-muted/40 text-muted-foreground border-border/60";
    default:
      return "bg-rose-500/10 text-rose-300 border-rose-500/40";
  }
}

export function IdentityBackfillCard({ adminHeadersFn = () => ({}) }) {
  const [tickersInput, setTickersInput] = useState("");
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [checkBusy, setCheckBusy] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [checkError, setCheckError] = useState(null);

  const parsed = _parseTickers(tickersInput);

  const runBackfill = async () => {
    setError(null);
    setResult(null);
    if (parsed.length === 0) {
      setError("Enter at least one ticker.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.post(
        "/admin/companies/identity/backfill",
        { tickers: parsed, force },
        { headers: adminHeadersFn() }
      );
      setResult({ ...res.data, ranAt: new Date() });
      toast.success(
        `Backfill done — ${res.data?.created || 0} created, ${res.data?.updated || 0} updated, ${res.data?.skipped_fresh || 0} skipped (fresh)`
      );
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        detail && typeof detail === "object" && detail.error
          ? `Backfill failed: ${detail.error}`
          : typeof detail === "string"
          ? detail
          : e?.message || "Backfill failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const runCheck = async () => {
    setCheckError(null);
    setCheckResult(null);
    if (parsed.length === 0) {
      setCheckError("Enter at least one ticker.");
      return;
    }
    setCheckBusy(true);
    try {
      const res = await api.get("/companies/identity", {
        params: { tickers: parsed.join(",") },
      });
      const ids = res?.data?.identities || {};
      const rows = parsed.map((tk) => ({ ticker: tk, ...ids[tk] }));
      setCheckResult({ rows, ranAt: new Date() });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Cache check failed";
      setCheckError(typeof msg === "string" ? msg : "Cache check failed");
    } finally {
      setCheckBusy(false);
    }
  };

  return (
    <div
      className="rounded-lg border border-border/70 bg-card/60 p-4 sm:p-5 space-y-4"
      data-testid="owner-identity-backfill-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-foreground">
            Company Identity Backfill
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl leading-relaxed">
            Backfill company names, logos and profile metadata for selected
            tickers. This uses FMP <span className="font-mono">/profile</span>{" "}
            data only. It does not run stock analysis or AI.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="identity-backfill-tickers" className="text-xs">
            Tickers
          </Label>
          <button
            type="button"
            onClick={() => setTickersInput(LAUNCH_TICKERS)}
            className="text-[11px] text-primary hover:underline"
            data-testid="owner-identity-backfill-use-launch"
          >
            Use launch tickers
          </button>
        </div>
        <Textarea
          id="identity-backfill-tickers"
          data-testid="owner-identity-backfill-tickers"
          value={tickersInput}
          onChange={(e) => setTickersInput(e.target.value)}
          placeholder={LAUNCH_TICKERS}
          rows={3}
          className="font-mono text-xs"
        />
        <div className="text-[11px] text-muted-foreground">
          {parsed.length > 0
            ? `${parsed.length} unique ticker${parsed.length === 1 ? "" : "s"} parsed`
            : "Comma, space or newline separated. Duplicates are deduped."}
        </div>
      </div>

      <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
        <Checkbox
          checked={force}
          onCheckedChange={(v) => setForce(!!v)}
          data-testid="owner-identity-backfill-force"
          className="mt-0.5"
        />
        <span className="min-w-0">
          <span className="font-medium text-foreground">
            Force refresh existing identities
          </span>
          <span className="block text-[11px] text-muted-foreground/90">
            Bypasses freshness checks and refetches profile data.
          </span>
        </span>
      </label>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          onClick={runBackfill}
          disabled={busy || parsed.length === 0}
          data-testid="owner-identity-backfill-run"
        >
          {busy ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
          Backfill identities
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={runCheck}
          disabled={checkBusy || parsed.length === 0}
          data-testid="owner-identity-backfill-check"
        >
          {checkBusy ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
          Check cached identities
        </Button>
      </div>

      {error ? (
        <div
          className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200"
          data-testid="owner-identity-backfill-error"
        >
          {error}
        </div>
      ) : null}

      {result ? (
        <div className="space-y-2" data-testid="owner-identity-backfill-result">
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Backfill result
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 text-xs">
            {[
              ["requested", result.requested],
              ["processed", result.processed],
              ["created", result.created],
              ["updated", result.updated],
              ["skipped_fresh", result.skipped_fresh],
              ["skipped_logo_missing", result.skipped_logo_missing_suppressed],
              ["skipped_fmp_off", result.skipped_fmp_disabled],
              ["failed", result.failed],
            ].map(([k, v]) => (
              <div
                key={k}
                className="rounded-md border border-border/60 bg-card/40 px-2 py-1.5"
                data-testid={`owner-identity-backfill-counter-${k}`}
              >
                <div className="text-[10px] uppercase text-muted-foreground tracking-wider">
                  {k.replace(/_/g, " ")}
                </div>
                <div className="font-mono text-sm text-foreground">{v ?? 0}</div>
              </div>
            ))}
          </div>
          <div className="rounded-md border border-border/60 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-secondary/40 text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-1.5">Ticker</th>
                  <th className="text-left px-3 py-1.5">Status</th>
                  <th className="text-left px-3 py-1.5">Has logo</th>
                  <th className="text-left px-3 py-1.5">Reason</th>
                </tr>
              </thead>
              <tbody>
                {(result.results || []).map((r) => (
                  <tr
                    key={r.ticker}
                    className="border-t border-border/40"
                    data-testid={`owner-identity-backfill-row-${r.ticker}`}
                  >
                    <td className="px-3 py-1.5 font-mono">{r.ticker}</td>
                    <td className="px-3 py-1.5">
                      <Badge
                        variant="outline"
                        className={`text-[10px] px-2 py-0 ${_statusStyle(r.status)}`}
                      >
                        {r.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5">
                      {r.has_logo ? (
                        <span className="text-emerald-400">yes</span>
                      ) : (
                        <span className="text-muted-foreground">no</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[280px]">
                      {r.reason || "\u2014"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {checkError ? (
        <div
          className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-xs text-rose-200"
          data-testid="owner-identity-backfill-check-error"
        >
          {checkError}
        </div>
      ) : null}

      {checkResult ? (
        <div
          className="space-y-2"
          data-testid="owner-identity-backfill-check-result"
        >
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
            Cached identities (cache-only, no FMP call)
          </div>
          <div className="rounded-md border border-border/60 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-secondary/40 text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-1.5">Ticker</th>
                  <th className="text-left px-3 py-1.5">Logo</th>
                  <th className="text-left px-3 py-1.5">Name</th>
                  <th className="text-left px-3 py-1.5">Logo URL?</th>
                  <th className="text-left px-3 py-1.5">Source</th>
                </tr>
              </thead>
              <tbody>
                {checkResult.rows.map((r) => (
                  <tr
                    key={r.ticker}
                    className="border-t border-border/40"
                    data-testid={`owner-identity-backfill-check-row-${r.ticker}`}
                  >
                    <td className="px-3 py-1.5 font-mono">{r.ticker}</td>
                    <td className="px-3 py-1.5">
                      {r.logo_url ? (
                        <img
                          src={r.logo_url}
                          alt={`${r.name || r.ticker} logo`}
                          className="h-6 w-6 rounded-md border border-border/60 bg-white object-contain p-0.5"
                          loading="lazy"
                          onError={(ev) => {
                            ev.currentTarget.style.display = "none";
                          }}
                          data-testid={`owner-identity-backfill-check-logo-${r.ticker}`}
                        />
                      ) : (
                        <span className="text-[10px] text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 truncate max-w-[260px]">
                      {r.name || "—"}
                    </td>
                    <td className="px-3 py-1.5">
                      {r.logo_url ? (
                        <span className="text-emerald-400">yes</span>
                      ) : (
                        <span className="text-muted-foreground">no</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[220px]">
                      {r.source || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default IdentityBackfillCard;
